/**
 * Shared UI primitives used across CMSEditor section renderers.
 * Kept in one file so consumers only need one import path.
 */
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash2, Plus, Eye } from "lucide-react";

export const Grid2 = ({ children }) => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>
);

export const SubTitle = ({ children }) => (
  <div className="mb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">{children}</div>
);

export const Hint = ({ children }) => (
  <p className="text-xs leading-relaxed text-muted-foreground">{children}</p>
);

export const Code = ({ children }) => (
  <code className="rounded bg-muted px-1 py-0.5 text-[11px]">{children}</code>
);

export function Field({ label, children }) {
  return (
    <div>
      <Label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

export function FieldFull({ label, children }) {
  return (
    <div className="md:col-span-2">
      <Label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

export function ColorField({ label, path, draft, set }) {
  const keys = path.split(".");
  const val = keys.reduce((o, k) => o?.[k], draft) || "#000000";
  return (
    <div>
      <Label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-2">
        <div className="relative h-9 w-14 shrink-0 overflow-hidden rounded border border-border" style={{ background: val }}>
          <input
            type="color"
            value={val}
            onChange={(e) => set(path, e.target.value)}
            className="absolute inset-0 cursor-pointer opacity-0"
            data-testid={`cms-color-${keys[keys.length - 1]}`}
          />
        </div>
        <Input value={val} onChange={(e) => set(path, e.target.value)} className="font-mono text-xs" />
      </div>
    </div>
  );
}

export function ThemePreset({ label, onClick }) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      data-testid={`cms-preset-${label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      {label}
    </Button>
  );
}

/* Live palette preview strip (public theme) */
export function ThemePalettePreview({ theme }) {
  const chips = [
    { name: "Background", color: theme.background },
    { name: "Primary", color: theme.primary },
    { name: "Surface", color: theme.surface },
    { name: "Text", color: theme.text },
    { name: "Border", color: theme.border },
    { name: "Dark", color: theme.dark },
  ];
  return (
    <div className="rounded-md border border-border/60 bg-muted/20 p-4">
      <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
        <Eye size={12} /> Live palette preview
      </div>
      <div className="flex flex-wrap gap-2">
        {chips.map((c) => (
          <div key={c.name} className="flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5">
            <div className="h-6 w-10 rounded ring-1 ring-black/10" style={{ background: c.color || "#000000" }} />
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{c.name}</div>
              <div className="font-mono text-[10px]">{c.color || "—"}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Live login preview (mini card) — for customer & staff themes */
export function LoginThemePreview({ kind, theme }) {
  if (kind === "customer") {
    const from = theme.gradient_from || "#EA580C";
    const to = theme.gradient_to || "#F59E0B";
    const accent = theme.accent || "#F97316";
    const cardBg = theme.card_bg || "#FFFFFF";
    const panelBg = theme.panel_bg || "#FFF9F5";
    return (
      <div className="rounded-md border border-border/60 bg-muted/20 p-4">
        <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
          <Eye size={12} /> Live sign-in preview
        </div>
        <div className="grid grid-cols-[1fr_1.2fr] overflow-hidden rounded-lg ring-1 ring-black/10">
          <div className="p-4" style={{ background: `linear-gradient(135deg, ${from}, ${to})`, color: theme.text_on_gradient || "#FFFFFF" }}>
            <div className="text-[10px] font-bold uppercase tracking-widest opacity-80">Doorstep diesel</div>
            <div className="mt-2 font-display text-lg font-black leading-tight">Fuel where you work</div>
          </div>
          <div className="p-4" style={{ background: panelBg }}>
            <div className="rounded-lg p-3 shadow-sm" style={{ background: cardBg }}>
              <div className="mb-2 h-2 w-14 rounded" style={{ background: "#E5E7EB" }} />
              <div className="mb-3 h-6 w-full rounded" style={{ background: "#F1F5F9" }} />
              <div className="mb-3 h-6 w-full rounded" style={{ background: "#F1F5F9" }} />
              <div className="h-8 w-full rounded text-center text-[11px] font-bold leading-8 text-white" style={{ background: accent }}>Sign in</div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  const bg = theme.bg || "#020617";
  const cardBg = theme.card_bg || "#0F172A";
  const accent = theme.accent || "#F97316";
  const orb1 = theme.orb_1 || "#F97316";
  const orb2 = theme.orb_2 || "#0EA5E9";
  const text = theme.text || "#F1F5F9";
  const muted = theme.muted_text || "#94A3B8";
  return (
    <div className="rounded-md border border-border/60 bg-muted/20 p-4">
      <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
        <Eye size={12} /> Live sign-in preview
      </div>
      <div className="relative overflow-hidden rounded-lg p-4 ring-1 ring-black/10" style={{ background: bg, color: text }}>
        <div className="absolute -left-8 -top-8 h-24 w-24 rounded-full blur-2xl opacity-40" style={{ background: orb1 }} />
        <div className="absolute -bottom-8 -right-8 h-28 w-28 rounded-full blur-2xl opacity-30" style={{ background: orb2 }} />
        <div className="relative rounded-lg p-4" style={{ background: cardBg }}>
          <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: muted }}>Staff sign-in</div>
          <div className="mt-1 font-display text-lg font-black">Command your operation</div>
          <div className="mt-3 h-6 w-full rounded" style={{ background: "rgba(255,255,255,0.06)" }} />
          <div className="mt-2 h-6 w-full rounded" style={{ background: "rgba(255,255,255,0.06)" }} />
          <div className="mt-3 h-8 w-full rounded text-center text-[11px] font-bold leading-8 text-white" style={{ background: accent }}>Verify &amp; sign in</div>
        </div>
      </div>
    </div>
  );
}

export function ArrayEditor({ items, onChange, columns, addLabel, newRow }) {
  const upd = (i, k, v) => onChange(items.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const rm = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, newRow()]);
  return (
    <div className="space-y-3">
      {items.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          Nothing here yet. Click <span className="font-semibold">Add</span> below to create your first entry.
        </div>
      )}
      {items.map((row, i) => (
        <Card key={`arr-${i}`} className="border-border/60">
          <CardContent className="p-3">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-12">
              {columns.map((c) => (
                <div key={c.key} className={c.size === "sm" ? "md:col-span-1" : c.textarea ? "md:col-span-6" : "md:col-span-3"}>
                  <Label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{c.label}</Label>
                  {c.textarea
                    ? <Textarea rows={2} value={row[c.key] ?? ""} onChange={(e) => upd(i, c.key, e.target.value)} />
                    : <Input value={row[c.key] ?? ""} onChange={(e) => upd(i, c.key, c.key === "step" ? Number(e.target.value) : e.target.value)} />}
                </div>
              ))}
              <div className="md:col-span-1 md:flex md:items-end md:justify-end">
                <Button variant="ghost" size="sm" onClick={() => rm(i)} data-testid={`cms-arr-del-${i}`}>
                  <Trash2 size={12} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      <Button variant="outline" onClick={add}>
        <Plus size={12} className="mr-1" /> {addLabel}
      </Button>
    </div>
  );
}
